import React, { useState, useEffect } from "react"
import { ChevronUpIcon, ChevronDownIcon } from "@heroicons/react/24/outline"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import ProgressBar from "aldoo-ra/ProgressBar"
import Tooltip from "aldoo-ra/Tooltip"
import { showToast } from "aldoo-ra/Toast"
import getRealtimeDeploymentController from "./realtime-deployment-controller"

const controller = getRealtimeDeploymentController()

// Global toast management for stacking
let activeDeploymentToasts = new Map() // appId -> toastId
let stackedToasts = new Set() // Track stacked toast IDs

export const showDeploymentUpdateToast = (app) => {

  // If this app already has a toast, don't create another one
  // The detection system should prevent duplicates, but this is a safety check
  if (activeDeploymentToasts.has(app.id)) {
    return activeDeploymentToasts.get(app.id)
  }

  const updateCount = app.gitState?.updates?.length || 0
  const updates = app.gitState?.updates || []
  
  // Sort updates by date like the Available Updates dialog does
  const sortedUpdates = updates.sort((a, b) => new Date(b.date) - new Date(a.date))
  const latestUpdate = sortedUpdates[0]
  
  // Get commit message from available updates (same as dialog)
  const commitMessage = latestUpdate?.subject || 'No release notes available'
  
  // Create a summary of commit messages for the toast message
  const getUpdatesSummary = () => {
    if (updateCount === 0) return 'No updates available'
    const message = latestUpdate?.subject
    return message || 'Update available'
  }


  const toastId = showToast({
    title: (
      <Tooltip content={commitMessage} position="bottom">
        <span className="cursor-help hover:text-admin_primary transition-colors">
          App Update
        </span>
      </Tooltip>
    ),
    message: null,
    variant: 'admin_info',
    persistent: true, // Indefinite until dismissed
    position: 'TOP_RIGHT',
    children: <DeploymentToastContent app={app} />,
    onClose: (id) => {
      // Clean up tracking when toast is closed
      activeDeploymentToasts.delete(app.id)
      stackedToasts.delete(id)
    }
  })


  // Track this toast
  activeDeploymentToasts.set(app.id, toastId)
  stackedToasts.add(toastId)

  return toastId
}

export const dismissDeploymentToast = (appId) => {
  const toastId = activeDeploymentToasts.get(appId)
  
  if (toastId) {
    // Remove from Toast system - this will trigger onClose callback
    document.dispatchEvent(new CustomEvent('removeToast', { detail: { id: toastId } }))
  }
}

const DeploymentToastContent = ({ app }) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const [updateStatus, setUpdateStatus] = useState(null)
  const [deploymentState, setDeploymentState] = useState(app.deploymentState || {})
  const [currentApp, setCurrentApp] = useState(app)
  
  // Calculate updateCount inside the component
  const updateCount = currentApp.gitState?.updates?.length || 0

  // Listen for real-time updates using the same system as deployment viewer
  useEffect(() => {
    let cleanup = null

    const setupRealtimeUpdates = async () => {
      try {
        // Initialize realtime controller connection if needed
        if (!controller.isInitialized) {
          controller.initialize()
        }

        // Subscribe to deployment environment updates
        await controller.subscribeToEnvironment('production', (data) => {
          // Handle app status updates from realtime channel
          if (data.result && data.data) {
            const appData = Object.values(data.data).find(appItem => appItem.id === app.id)
            if (appData) {
              // Preserve gitState.updates for available updates display
              const updatedApp = {
                ...appData,
                gitState: {
                  ...appData.gitState,
                  updates: appData.gitState?.updates || app.gitState?.updates || []
                }
              }
              setCurrentApp(updatedApp)
              if (appData.deploymentState) {
                setDeploymentState(appData.deploymentState)
              }
            }
          }
        })

        // Set up task update listeners directly on the Socket.io instance
        const socket = controller.socket
        if (socket) {
          const handleTaskUpdate = (taskStatus) => {
            // Check if this update is for our app's task
            if (taskStatus && taskStatus.taskId && 
                (currentApp.updateTask === taskStatus.taskId || 
                 (currentApp.updateTask && currentApp.updateTask.id === taskStatus.taskId))) {
              
              
              // Update deployment state with real-time progress
              setDeploymentState(prevState => {
                const newState = { ...prevState }
                
                if (taskStatus.progress !== undefined) {
                  newState.progress = taskStatus.progress
                }
                
                if (taskStatus.status) {
                  const newStatus = taskStatus.status === 'completed' ? 'completed' : 'running'
                  newState.status = newStatus
                }
                
                if (taskStatus.currentStep || taskStatus.activity) {
                  newState.currentStep = taskStatus.currentStep || taskStatus.activity
                }
                
                if (taskStatus.status === 'completed') {
                  newState.completedAt = new Date().toISOString()
                  
                  // Clear the updates when deployment completes to fix the "1 update" bug
                  setCurrentApp(prevApp => ({
                    ...prevApp,
                    gitState: {
                      ...prevApp.gitState,
                      updates: [],
                      status: 'updated'
                    }
                  }))
                }
                
                return newState
              })
              
              // Clear local updateStatus when real-time takes over
              if (updateStatus?.status === 'starting' || updateStatus?.status === 'running') {
                setUpdateStatus(null)
              }
            }
          }

          // Listen for the same events as the deployment viewer
          socket.on('deployment:task_update', handleTaskUpdate)
          socket.on('deployment:task_progress', handleTaskUpdate) 
          socket.on('deployment:task_completed', handleTaskUpdate)

          // Set cleanup function
          cleanup = () => {
            socket.off('deployment:task_update', handleTaskUpdate)
            socket.off('deployment:task_progress', handleTaskUpdate)
            socket.off('deployment:task_completed', handleTaskUpdate)
          }
        }

      } catch (error) {
        console.error('Failed to subscribe to deployment updates:', error)
      }
    }

    setupRealtimeUpdates()

    // Cleanup function
    return () => {
      if (cleanup) {
        cleanup()
      }
    }
  }, [app.id, currentApp.updateTask])

  // Watch for deployment completion to show success state (no auto-close)
  useEffect(() => {
    // Just update the UI to show completion - don't auto-close
    if (deploymentState.status === 'completed' && deploymentState.completedAt) {
      // Deployment completed - user can manually close if they want
    }
  }, [deploymentState.status, deploymentState.completedAt, app.id])

  const handleRunUpdate = async () => {
    try {
      setUpdateStatus({
        status: "starting",
        activity: "Starting deployment...",
        progress: 0
      })

      // Check if controller is initialized before using it
      if (!controller.isInitialized) {
        throw new Error("Deployment system not ready. Please refresh the page and try again.")
      }

      // Use controller to start deployment
      const response = await controller.runAppUpdate(app.id)

      if (!response?.success) {
        throw new Error(response?.error || "Failed to start deployment")
      }

      // Store task ID for tracking
      if (response.data?.task_id) {
        app.updateTask = response.data.task_id
        // Also update currentApp state to trigger useEffect
        setCurrentApp(prevApp => ({
          ...prevApp,
          updateTask: response.data.task_id
        }))
      }

      setUpdateStatus({
        status: "running",
        activity: "Deployment in progress...",
        progress: 0
      })

    } catch (error) {
      setUpdateStatus({
        error: error.message,
        status: "error"
      })
    }
  }

  const getInternalStatus = () => {
    if (deploymentState?.status === "running") return "deploying"
    if (updateStatus?.status === "starting") return "starting"
    if (updateStatus?.status === "running") return "updating"
    if (updateStatus?.status === "error") return "failed"
    if (deploymentState?.status === "completed") return "completed"
    if (currentApp.gitState?.status === "pending") return "pending"
    return "updated"
  }

  const internalStatus = getInternalStatus()
  const isDeploying = internalStatus === "deploying" || internalStatus === "starting" || internalStatus === "updating"

  return (
    <div className="mt-3">
      {/* App Name and Controls */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full ${
            internalStatus === 'pending' ? 'bg-admin_progress' :
            internalStatus === 'deploying' || internalStatus === 'starting' || internalStatus === 'updating' ? 'bg-admin_primary' :
            internalStatus === 'completed' ? 'bg-admin_success' :
            internalStatus === 'failed' ? 'bg-admin_error' :
            'bg-admin_success'
          }`} />
          
          <Typography 
            variant="admin_txt1" 
            className="text-admin_text font-medium"
          >
            {currentApp.name}
            {internalStatus === 'failed' && ' - ❌ Failed'}
            {(internalStatus === 'deploying' || internalStatus === 'starting' || internalStatus === 'updating') && ' - Deploying...'}
          </Typography>
        </div>

        <div className="flex items-center gap-2">
          {!isDeploying && updateCount > 0 && internalStatus !== 'completed' && (
            <Button
              variant="admin_primary"
              onClick={handleRunUpdate}
              disabled={isDeploying}
            >
              {updateCount} update{updateCount > 1 ? 's' : ''}
            </Button>
          )}
        </div>
      </div>

      {/* Progress Bar when deploying or completed */}
      {(isDeploying || internalStatus === 'completed') && (
        <div className="mt-6 mb-3">
          <ProgressBar
            title={
              internalStatus === 'completed' 
                ? "✅ Deployment Complete!" 
                : (deploymentState?.currentStep || updateStatus?.activity || "Deploying...")
            }
            variant={internalStatus === 'completed' ? "success" : "default"}
            progress={
              internalStatus === 'completed' 
                ? 1 
                : ((deploymentState?.progress || updateStatus?.progress || 0) / 100)
            }
          />
        </div>
      )}

      {/* Expand Button - only show if there are messages to show */}
      {updateCount > 0 && (
        <div className="flex justify-center mb-3">
          <Button
            variant="admin_icon"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? (
              <ChevronUpIcon className="w-4 h-4 text-admin_text_secondary" />
            ) : (
              <ChevronDownIcon className="w-4 h-4 admin_text_secondary" />
            )}
          </Button>
        </div>
      )}

      {/* Error Display */}
      {updateStatus?.error && (
        <div className="bg-admin_error/10 border border-admin_error/20 rounded p-2 mb-3">
          <Typography 
            variant="admin_txt1" 
            className="text-admin_error"
          >
            {updateStatus.error}
          </Typography>
        </div>
      )}

      {/* Expanded Content */}
      {isExpanded && updateCount > 0 && (
        <div className="border-t border-admin_bordered pt-3 mt-3">
          <div className="max-h-32 overflow-y-auto">
            {/* Show available updates (same as Available Updates dialog) */}
            {currentApp.gitState?.updates && currentApp.gitState.updates.length > 0 && (() => {
              const sortedUpdates = [...currentApp.gitState.updates].sort((a, b) => new Date(b.date) - new Date(a.date))
              return sortedUpdates.slice(0, 5).map((update, index) => (
                <div key={index} className="mb-1">
                  <Typography 
                    variant="admin_txt2" 
                    className="text-admin_text"
                  >
                    • {update.subject}
                  </Typography>
                </div>
              ))
            })()}
          </div>
        </div>
      )}
    </div>
  )
}

export default DeploymentToastContent